import { useEffect, useRef, useState } from 'react';
import { aiGatewayService } from '@/services/aiGateway';
import type {
  ScribeLedgerFact,
  ScribeLedgerUpdatedPayload,
} from '@/services/aiGateway/scribeEvents';

/**
 * Live view of the rolling clinical fact ledger for one capture session.
 *
 * The backend folds the transcript into a structured ledger every
 * `LEDGER_INTERVAL_CHUNKS` (~2 minutes of audio) and now pushes the result
 * over the existing scribe socket. This hook holds the latest ACTIVE set so
 * a rail can show the doctor what has been picked up without them having to
 * read the transcript.
 *
 * Three things worth knowing before using this:
 *
 * 1. **Replace, never append.** Each payload is the whole active set. A
 *    fact can be retracted or superseded as the consult goes on ("no fever"
 *    → "fever last night"), and the backend drops those before emitting. If
 *    you accumulate instead, the retracted fact stays on screen and the
 *    display contradicts the note that gets written.
 * 2. **It is flag-gated and may never arrive.** `EXTENDED_RECORDING_LEDGER`
 *    is off by default, and the ledger has not been validated against a
 *    live model. Treat an empty list as the normal case and render nothing
 *    rather than an empty state.
 * 3. **The cadence is minutes, not seconds.** Do not build anything that
 *    reads as real-time; the first facts land a couple of minutes in.
 */
export function useLedgerFacts(sessionId: string | null | undefined): {
  facts: ScribeLedgerFact[];
  updatedAt: string | null;
} {
  const [facts, setFacts] = useState<ScribeLedgerFact[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);

  // Read the live session id inside the handler rather than resubscribing
  // on every change — same pattern as the transcript-partial subscription
  // in useLiveRecording.
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;

  // Guards against an out-of-order delivery walking the rail backwards.
  const versionRef = useRef<number>(-1);

  useEffect(() => {
    const off = aiGatewayService.on(
      'scribe.ledger-updated',
      (data: ScribeLedgerUpdatedPayload) => {
        // RecordingDock and RecordOnPhoneModal share this socket; drop
        // anything belonging to the other surface.
        if (
          data?.sessionId &&
          sessionIdRef.current &&
          data.sessionId !== sessionIdRef.current
        ) {
          return;
        }
        const version = data?.version ?? 0;
        if (version < versionRef.current) return;
        versionRef.current = version;
        setFacts(Array.isArray(data?.facts) ? data.facts : []);
        setUpdatedAt(data?.updatedAt ?? null);
      },
    );
    return () => {
      off();
    };
  }, []);

  // A new session starts from nothing — otherwise the previous patient's
  // facts sit on screen until the first fold of the new consult lands,
  // which is a couple of minutes of showing the wrong patient's data.
  useEffect(() => {
    setFacts([]);
    setUpdatedAt(null);
    versionRef.current = -1;
  }, [sessionId]);

  return { facts, updatedAt };
}
